import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from "@angular/common";
import { CookieService } from 'ngx-cookie-service'; // Import the service
import { LoanApplicationSteps } from "../models/loan-application-step/loan-application-steps";
import { Loan } from "../models/loan";
import { Customer } from "../../customer/models/customer";

@Injectable({
  providedIn: 'root'
})
export class LoanApplicationService {
  private cookieService = inject(CookieService);
  private platformId = inject(PLATFORM_ID);

  // Helper to determine cookie settings
  private get cookieOptions() {
    return {
      path: '/',
      sameSite: 'Lax' as const,
      // Secure should be true in production (HTTPS)
      secure: isPlatformBrowser(this.platformId) ? window.location.protocol === 'https:' : false
    };
  }

  setApplicationStepDataInStorage(applicationStep: LoanApplicationSteps, data: any): void {
    // We stringify the data just like before
    const value = JSON.stringify(data);
    this.cookieService.set(applicationStep, value, this.cookieOptions);
  }

  getApplicationStepFromStorage(applicationStep: LoanApplicationSteps): any | null {
    const rawData = this.cookieService.get(applicationStep);

    if (rawData) {
      try {
        return JSON.parse(rawData);
      } catch (e) {
        console.error(`Error parsing cookie for`, applicationStep, e);
        return null;
      }
    }
    return null;
  }

  removeApplicationStepFromStorage(applicationStep: LoanApplicationSteps): void {
    this.cookieService.delete(applicationStep, '/');
  }

  removeAllApplicationStepsFromStorage(): void {
    // Delete specifically our loan step cookies
    for (const loanStep of Object.values(LoanApplicationSteps)) {
      this.removeApplicationStepFromStorage(loanStep as LoanApplicationSteps);
    }
  }

  // ... your getPreparedLoan and getPreparedCustomer logic remains the same
  // because they now call the updated getApplicationStepFromStorage internally.

  getPreparedLoan(): Loan | null {
    let loan: Loan | null = null;
    const steps = Object.values(LoanApplicationSteps);

    for (const loanStep of steps) {
      const stepData = this.getApplicationStepFromStorage(loanStep as LoanApplicationSteps);
      if (stepData) {
        loan = Object.assign({}, loan, stepData) as Loan;
      }
    }

    if (loan) {
      const personalInfoStepData = this.getApplicationStepFromStorage(LoanApplicationSteps.PersonalInformation);
      if (personalInfoStepData) {
        loan.customerIdCardIssueDate = new Date(
          personalInfoStepData.customerIdCardIssueDateYear,
          personalInfoStepData.customerIdCardIssueDateMonth,
          personalInfoStepData.customerIdCardIssueDateDay,
        );
      }
    }
    return loan;
  }

  getPreparedCustomer(): Customer | null {
    let loan = this.getPreparedLoan();
    if (!loan) return null;

    return Object.fromEntries(
      Object.entries(loan).map(([key, value]) => [
        key.startsWith("customer") ? key.replace("customer", "").replace(/^\w/, c => c.toLowerCase()) : key,
        value
      ])
    ) as Customer;
  }
}
